<template>
  <div class="infinite-carousel" :class="{ disabled }">
    <div class="infinite-head" :class="{ container: title }">
      <slot name="heading" class="heading" />
      <component
        :is="heading" v-show="title" class="h4"
        :class="headingClass" v-html="title"
      />
      <slot name="mobile-description" class="mobile description" />
      <div v-show="!disabled" class="controls">
        <CaretLeftCircle @click="prev" />
        <CaretRightCircle @click="next" />
      </div>
    </div>
    <slot name="description" class="description" />
    <div class="carousel">
      <div
        ref="inner" v-swipe.right="prev" v-swipe.left="next"
        class="inner" :style="!disabled ? innerStyles : {}"
      >
        <slot name="slide" />
      </div>
    </div>
  </div>
</template>

<script>
import CaretLeftCircle from '../assets/svg/caret-left-circle.svg?vue-component';
import CaretRightCircle from '../assets/svg/caret-right-circle.svg?vue-component';

export default {
  components: {
    CaretLeftCircle,
    CaretRightCircle,
  },
  props: {
    align: { type: String, default: 'center', required: false },
    title: { type: String, default: '', required: false },
    heading: { type: String, default: 'p', required: false },
    headingClass: { type: String, default: '', required: false },
    offset: { type: Number, default: 2, required: false },
    disabled: Boolean,
  },
  data() {
    return {
      innerStyles: {},
      step: '',
      transitioning: false,
      center: 0,
      screenWidth: 0,
    };
  },
  computed: {
    slides() {
      return this.$slots.slide;
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.layout': function () {
      this.$nextTick(() => this.setStep(true));
    },
  },
  mounted() {
    window.addEventListener('resize', () => this.setStep());
    this.$nextTick(() => this.setStep(true));
  },
  unmounted() {
    window.removeEventListener('resize', this.setStep);
  },
  methods: {
    setStep(force = false) {
      if (this.screenWidth !== window.screen.width || force) {
        this.screenWidth = window.screen.width;
        this.$nextTick(() => {
          const offset = this.offset || 0;
          const wrapper = this.$el.querySelector('.inner');
          const lastSlide = wrapper.lastChild;
          wrapper.insertBefore(lastSlide, wrapper.firstChild);
          const slide = this.slides[0].elm;
          const style = slide.currentStyle || window.getComputedStyle(slide);
          const slideWidth = slide.offsetWidth;
          const halfScreen = document.body.clientWidth / 2;
          const slideOffset = +style.marginRight.replace('px', '');
          const elementFullWidth = slideWidth + slideOffset;
          const elementsFittedInHalfScreen = Math.ceil(halfScreen / elementFullWidth);
          const exactWithOfElementsToCenter = elementsFittedInHalfScreen * elementFullWidth + (slideWidth / 2);

          this.step = slideWidth + slideOffset;

          // centered alignment (default)
          this.center = exactWithOfElementsToCenter - halfScreen;

          // container alignment
          if (this.align === 'container') {
            this.center = (((document.body.clientWidth - document.querySelector('main .container').offsetWidth) / 2) - (this.step * (offset + 1))) * -1;
          }

          // left alignment
          if (this.align === 'left') {
            this.center = 0;
          }

          this.resetTranslate();
        });
      }
    },
    next() {
      if (this.transitioning) return;
      this.transitioning = true;
      this.moveLeft();
      this.afterTransition(() => {
        this.resetTranslate();

        if (this.transitioning) {
          const wrapper = this.$el.querySelector('.inner');
          const firstSlide = wrapper.firstChild;
          firstSlide.remove();
          wrapper.appendChild(firstSlide);
        }
        this.transitioning = false;
      });
    },
    prev() {
      if (this.transitioning) return;
      this.transitioning = true;

      const wrapper = this.$el.querySelector('.inner');
      const lastSlide = wrapper.lastChild;
      const clone = lastSlide.cloneNode(true);
      wrapper.insertBefore(clone, wrapper.firstChild);
      clone.style.position = 'absolute';
      clone.style.right = '100%';
      const leftDefault = clone.style.left;
      clone.style.left = 'auto';

      this.moveRight();
      this.afterTransition(() => {
        this.resetTranslate();

        if (this.transitioning) {
          lastSlide.remove();
          clone.style.left = leftDefault;
          clone.style.removeProperty('right');
          clone.style.removeProperty('position');
        }
        this.transitioning = false;
      });
    },
    moveLeft() {
      const value = +this.step;

      this.innerStyles = {
        transform: `translateX(-${this.center + value}px)`,
      };
    },
    moveRight() {
      const value = +this.step;

      this.innerStyles = {
        transform: `translateX(${(this.center - value) * -1}px)`,
      };
    },
    afterTransition(callback) {
      const listener = (e) => {
        // callback only when ending transition is transform and is on transitioned div
        if (e.propertyName === 'transform' && e.target.classList.contains('inner')) {
          callback();
          this.$refs.inner.removeEventListener('transitionend', listener);
        }
      };
      this.$refs.inner.addEventListener('transitionend', listener);
    },
    resetTranslate() {
      if (this.center < 0) {
        this.center *= -1;
      }

      this.innerStyles = {
        transition: 'none',
        transform: `translateX(-${this.center}px)`,
      };
    },
  },
};
</script>

<style lang="scss">
.infinite-carousel {
  .description {
    &.mobile {
      display: none;
    }
  }

  .controls {
    text-align: center;
    margin: var(--spacing-24) 0 var(--spacing-36);

    svg {
      cursor: pointer;

      circle[fill] {
        fill: transparent;
      }

      circle[stroke] {
        stroke: var(--main-light);
      }

      path[stroke] {
        stroke: var(--main-light);
      }

      &:first-of-type {
        margin-right: var(--spacing-14);
      }
    }
  }

  .carousel {
    margin: 0 auto;
    width: 100%;
    overflow: hidden;

    .inner {
      transition: transform 1s;
      white-space: nowrap;
      text-align: center;

      > * {
        margin-right: 10px;
        border-radius: 4px;
        align-items: center;
        justify-content: center;
        display: inline-flex;
        overflow: hidden;
        white-space: normal;
        text-align: left;

        > * {
          flex: auto;
        }
      }
    }

    /* optional */
    button {
      margin-right: 5px;
      margin-top: 10px;
    }
  }
}
</style>
